import React, { forwardRef, useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import { useTranslation } from "next-i18next"

import { smoothScroll } from "@/utils/helper"
import CONSTANTS from "@/constants/index"

import { addAnalyticsShowHidePasswordClick } from "@/components/checkout/v2/analytics"
import {
  HIDE_PASSWORD,
  SHOW_PASSWORD,
} from "@/components/checkout/v2/analytics/constants"

import Styles from "@/components/core/Input/Input.module.scss"

const Input = forwardRef((props, ref) => {
  const inputRef = useRef(null)
  const errorRef = useRef(null)
  const { t } = useTranslation()
  const [type, setType] = useState(props.type ?? "text")
  const [toggle, setToggle] = useState(false)
  const [showList, setShowList] = useState(false)
  const [selectedListItem, setListItem] = useState("")
  const [focus, setFocus] = useState(false)
  const {
    showError,
    errorMessage,
    id,
    className = "",
    placeholder = "",
    autoComplete = "off",
    isCheckoutPage = false,
  } = props

  useEffect(() => {
    if (toggle) setType("text")
    else setType("password")
  }, [toggle])

  useEffect(() => {
    if (props?.focus) {
      inputRef?.current?.focus({ preventScroll: true })
    }
  }, [props?.focus])

  useEffect(() => {
    if (showError && errorMessage) {
      errorRef.current.focus()
    }
  }, [showError, errorMessage])

  useEffect(() => {
    const containerTarget = e => {
      if (
        showList &&
        !document.getElementById(`${props.id}-wrapper`)?.contains(e.target)
      )
        setShowList(false)
    }
    window.addEventListener("click", containerTarget)

    return () => {
      window.removeEventListener("click", containerTarget)
    }
  }, [showList])

  useEffect(() => {
    if (props.value?.length > 0 && props.suggestions?.length > 0) {
      setShowList(true)
      selectListItem()
    } else setShowList(false)
  }, [props.value, props.suggestions])

  useEffect(() => {
    if (showList && !selectedListItem) setActivedescendant()
  }, [showList, selectedListItem])

  const selectListItem = () => {
    const listItems = [...props.suggestions]
    const firstMatchedItemIndex = listItems.findIndex(item =>
      item?.includes(props.value)
    )

    if (firstMatchedItemIndex >= 0) {
      setListItem(`suggestion-list-item-${firstMatchedItemIndex}`)
      return
    }

    setActivedescendant()
  }

  const setActivedescendant = () =>
    setListItem(
      document.getElementById(`${props.id}-list`)?.childNodes[0].id ?? ""
    )

  const inputClasses = cx(
    "input-field__border",
    props.disabled ? "--disabled" : null,
    props.showError ? "--with-error" : null,
    focus ? "--focused" : null
  )

  const i18n = {
    show: t("kf.auth.label.show"),
    hide: t("kf.auth.label.hide"),
    resultsText: t("kf.input.aria.resultsText"),
  }

  const handleSelectSuggestion = (e, item) => {
    e?.preventDefault()
    props.onSelect?.(item)
    setShowList(false)
  }

  const handleSelectSuggestionByElement = optionElement => {
    const selectedIndex = Number(optionElement?.dataset?.index)
    if (!Number.isNaN(selectedIndex)) {
      handleSelectSuggestion(null, props.suggestions?.[selectedIndex])
    }
  }

  const focusInput = () => document.getElementById(props.id)?.focus()

  const handleSuggestionKeyDown = (e, item) => {
    const { KEYCODE } = CONSTANTS
    const li = e.currentTarget
    const preventKeys = new Set([KEYCODE.ENTER, KEYCODE.DOWN, KEYCODE.UP])
    if (preventKeys.has(e.keyCode)) e.preventDefault()
    switch (e.keyCode) {
      case KEYCODE.ENTER:
        handleSelectSuggestion(e, item)
        break
      case KEYCODE.DOWN:
        li.nextElementSibling?.focus()
        break
      case KEYCODE.UP:
        if (li.previousElementSibling) {
          li.previousElementSibling.focus()
        } else {
          focusInput()
        }
        break
      case KEYCODE.ESC:
        setShowList(false)
        focusInput()
        break
      default:
        break
    }
  }

  const handleKeyDown = e => {
    if (showList) {
      const { KEYCODE } = CONSTANTS
      const ele = [
        ...(document.getElementById(`${props.id}-list`)?.childNodes ?? []),
      ]
      if (!ele.length) return

      const index = ele.findIndex(item => item.id === selectedListItem)
      const selectedItem = index >= 0 ? ele[index] : null

      const setSelectedItem = nextIndex => {
        const nextItem = ele[nextIndex]
        if (!nextItem) return
        smoothScroll(nextItem)
        setListItem(nextItem.id)
      }

      const preventKeys = new Set([KEYCODE.UP, KEYCODE.DOWN, KEYCODE.ENTER])

      if (preventKeys.has(e.keyCode)) e.preventDefault()
      switch (e.keyCode) {
        case KEYCODE.ESC:
          setShowList(false)
          break
        case KEYCODE.UP:
          setSelectedItem(index > 0 ? index - 1 : 0)
          break
        case KEYCODE.DOWN:
          setSelectedItem(index < ele.length - 1 ? index + 1 : ele.length - 1)
          break
        case KEYCODE.SPACE:
          break
        case KEYCODE.ENTER:
          if (index === -1) break
          handleSelectSuggestionByElement(selectedItem)
          break
        default:
          break
      }
    }
  }

  const autoCompleteValue =
    autoComplete === "true" || autoComplete === true
      ? "on"
      : autoComplete === "false" || autoComplete === false
      ? "off"
      : autoComplete

  const handleClickHideShowPassword = () => {
    if (isCheckoutPage) {
      const btnClickAction = !toggle ? SHOW_PASSWORD : HIDE_PASSWORD
      addAnalyticsShowHidePasswordClick({ btnClickAction })
    }
  }

  const ariaActiveDescendant =
    props.showAriaOwnsriaActivedescendant !== false && showList
      ? selectedListItem || undefined
      : undefined

  const hasList = !props.autoComplete && showList

  const ariaControls =
    props?.showAriaOwns !== false && hasList ? `${props.id}-list` : undefined

  return (
    <div className={`${Styles.inputFieldWrapper} ${className}`}>
      <div
        className={` input-field ${props.customClass ? props.customClass : ""}`}
        id={`${props.id}-wrapper`}
        onClick={focusInput}
      >
        <div className={inputClasses}>
          {props.prefix && (
            <div className="input-field__prefix">{props.prefix}</div>
          )}
          <div className="input-field__value">
            <input
              ref={ref ?? inputRef}
              id={props.id}
              type={props.type === "password" ? type : props.type}
              value={props.value}
              name={props.name}
              autoComplete={
                props?.autoCompleteTextVal
                  ? props?.autoCompleteTextVal
                  : autoCompleteValue
              }
              maxLength={props.maxLength}
              disabled={props.disabled}
              placeholder={
                placeholder ? placeholder : props.label.replace(/\*/g, "")
              }
              onChange={props.onChange}
              onInput={props.onInput}
              onKeyUp={props.onKeyUp}
              onKeyDown={e => {
                handleKeyDown(e)
                props.onKeyDown?.(e)
              }}
              onFocus={e => {
                setFocus(true)
                props.onFocus?.(e)
              }}
              onBlur={e => {
                setFocus(false)
                props.onBlur?.(e)
              }}
              max={props.max}
              min={props.min}
              autoFocus={props.autoFocus}
              aria-required={
                props.ariaRequried ?? props.label.indexOf("*") > 0
                  ? true
                  : false
              }
              aria-activedescendant={ariaActiveDescendant}
              aria-readonly={props.readOnly ? "true" : "false"}
              aria-controls={ariaControls}
              // aria-expanded={props.autoComplete ? showList : "undefined"}
              aria-haspopup={props.autoComplete ? "listbox" : "false"}
              data-testid={"input"}
              aria-describedby={`${id}-error`}
            />
            <label htmlFor={props.id}>{props.label}</label>
          </div>
          {props.type === "password" && props.value?.length > 0 && (
            <div className="input-field__suffix">
              <span
                tabIndex="0"
                aria-label={
                  toggle
                    ? `${i18n.hide} ${props.label}`
                    : `${i18n.show} ${props.label}`
                }
                role="button"
                onClick={() => {
                  setToggle(!toggle)
                  handleClickHideShowPassword()
                }}
                className="input-field__toggle"
              >
                {toggle ? i18n.hide : i18n.show}
              </span>
            </div>
          )}
          {props.suffix && (
            <div className="input-field__suffix">{props.suffix}</div>
          )}
        </div>
        {showList && props.suggestions && (
          <ul
            id={`${props.id}-list`}
            className="input-field__suggestions"
            role="listbox"
            aria-label={i18n.resultsText}
          >
            {props.suggestions.map((item, i) => (
              <li
                key={i}
                id={`suggestion-list-item-${i}`}
                data-index={i}
                className={`input-field__list-item ${
                  selectedListItem === `suggestion-list-item-${i}`
                    ? "--hover"
                    : ""
                }`}
                aria-selected={
                  selectedListItem === `suggestion-list-item-${i}`
                    ? "true"
                    : "false"
                }
                role="option"
                tabIndex={0}
                onPointerDown={e => {
                  handleSelectSuggestion(e, item)
                }}
                onKeyDown={e => handleSuggestionKeyDown(e, item)}
              >
                <span className="input-field__list-value">{item}</span>
              </li>
            ))}
          </ul>
        )}
        {showError && errorMessage && (
          <span
            ref={errorRef}
            aria-live="polite"
            role="alert"
            id={`${id}-error`}
            className="input-field__error-message"
          >
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
})

Input.displayName = "Input"
Input.defaultProps = {
  label: "",
}

Input.propTypes = {
  onInput: PropTypes.func,
  onSelect: PropTypes.func,
  suggestions: PropTypes.array,
  id: PropTypes.string,
}

export default Input
